'use client';

import { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Button, Title, smallDotOverlayStyle } from '@puzzlio/ui';
import subscribeArt from '@puzzlio/ui/decorations/subscribe-calendar-games.png';
import { cn } from '@/app/lib/utils';
import { BackgroundBurst } from '@/app/games/components/BackgroundBurst';
import { useUser } from '@/app/providers/UserProvider';
import { useStripeSubscription } from '@/app/hooks/useStripe';
import { hasSubscriberAccess } from '@/app/auth/utils';

export const SubscriptionPromoSection: FC<{ className?: string }> = ({ className }) => {
  const user = useUser();
  const subscription = useStripeSubscription();

  // Keep this hidden until we've confirmed the user is *not* a subscriber, so the page can render
  // immediately without waiting on the subscription query. Existing subscribers never see it.
  if (subscription.isLoading) return null;
  if (user && hasSubscriberAccess(subscription.data, user)) return null;

  return (
    // A full-bleed band on a phone, a card from tablet up.
    // `isolate` keeps the burst's negative z-index inside the card: over its fill, under everything else.
    <section className={cn('bg-coral-200 relative isolate overflow-hidden md:rounded-xl', className)}>
      <div className="absolute inset-0" style={smallDotOverlayStyle('var(--coral-300)')} />

      <div className="relative flex flex-col items-center gap-5 px-5 pt-8 pb-9 md:flex-row-reverse md:gap-4 md:py-10 md:pr-8 md:pl-12">
        <div className="relative w-full max-w-[320px] shrink-0 md:w-[260px]">
          {/* Centred on the artwork wherever it sits, rather than at a guess at where that is. */}
          <BackgroundBurst
            color1="var(--coral-200)"
            color2="var(--coral-300)"
            isVisible={true}
            className="top-1/2 left-1/2 -z-10 aspect-square h-auto w-[1400px] -translate-x-1/2 -translate-y-1/2 opacity-70 md:w-[2000px]"
          />
          <Image
            src={subscribeArt}
            alt="A calendar of completed puzzles surrounded by Mosaic, Fields, Loopy River and Stars boards"
            sizes="320px"
            className="h-auto w-full"
          />
        </div>

        <div className="flex w-full flex-col items-center gap-2 md:flex-1 md:items-start">
          <Title
            className={cn(
              'leading-display text-center text-3xl tracking-[0.02em] whitespace-nowrap text-white md:text-left md:text-4xl lg:text-4xl',
              // Outlined display treatment: stroke painted behind the fill.
              '[-webkit-text-stroke:6px_#111111] [paint-order:stroke_fill] lg:[-webkit-text-stroke:8px_#111111]'
            )}
          >
            Any puzzle, any time
          </Title>
          <p className="font-quicksand max-w-[360px] text-center text-lg leading-snug font-semibold md:text-left">
            This week&apos;s puzzles are always free. Subscribe to explore every puzzle we&apos;ve ever made.
          </p>
          <Button asChild variant="gameplay" size="gameplay" color="orange" className="mt-3 w-full px-6 md:w-auto">
            <Link prefetch={false} href="/subscription?source=homepage">
              See plans
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
